import { useMutation } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  REMOVE_IGO,
  STEWARDSHIP_EXPORT_TURNAROUND_DAYS,
} from "@platform/feature-flags";

import { downloadBinaryFile } from "../../../../utils";

import { buildStewardshipExportWorkbook } from "./workbookBuilder";

import type { StewardshipExportMeta, StewardshipExportRow } from "./types";
import type { StewardshipDateBasis } from "../../useBrokerStewardship";

export type UseExportStewardshipArgs = {
  brokerId: string | undefined;
  brokerName: string;
  startDate: string | undefined;
  endDate: string | undefined;
  dateBasis: StewardshipDateBasis;
};

export type UseExportStewardshipResult = {
  exportReport: () => void;
  isExporting: boolean;
};

export function buildStewardshipExportFilename(
  meta: StewardshipExportMeta,
): string {
  const slug =
    meta.brokerName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "broker";

  return `${slug}-stewardship-${meta.dateBasis}-${meta.startDate}-${meta.endDate}.xlsx`;
}

export function useExportStewardship({
  brokerId,
  brokerName,
  startDate,
  endDate,
  dateBasis,
}: UseExportStewardshipArgs): UseExportStewardshipResult {
  const axiosInstance = useApi();
  const includeTurnaround = useFeatureIsOn(STEWARDSHIP_EXPORT_TURNAROUND_DAYS);
  const removeIgo = useFeatureIsOn(REMOVE_IGO);

  const mutation = useMutation({
    mutationFn: async (): Promise<StewardshipExportRow[]> => {
      const response = await axiosInstance.get<StewardshipExportRow[]>(
        `/consulting/v1/brokers/${brokerId}/stewardship/export`,
        {
          params: {
            ...(startDate ? { start_date: startDate } : {}),
            ...(endDate ? { end_date: endDate } : {}),
            // Omit for the submitted default, matching the report query.
            ...(dateBasis === "effective" ? { date_basis: "effective" } : {}),
          },
        },
      );

      return response.data;
    },
    onSuccess: async (rows) => {
      const blob = await buildStewardshipExportWorkbook(rows, {
        [STEWARDSHIP_EXPORT_TURNAROUND_DAYS]: includeTurnaround,
        [REMOVE_IGO]: removeIgo,
      });
      const buffer = await blob.arrayBuffer();
      const filename = buildStewardshipExportFilename({
        brokerName,
        // The applied range; an unset range exports the backend's resolved
        // default period, so fall back to a stable label for the filename.
        startDate: startDate ?? "ytd",
        endDate: endDate ?? "ytd",
        dateBasis,
      });

      downloadBinaryFile(buffer, filename);
    },
  });

  return {
    exportReport: () => mutation.mutate(),
    isExporting: mutation.isPending,
  };
}
